import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Button, Slider, Flex, ToggleButton, Text, TextInput } from '@leverege/ui-elements'
import { Section, LabelGroup } from '~/views'

import { GeotileActions, NotifyActions } from '~/actions/'
import ImageEditor from './ImageEditor'
import PlacementEditor from './PlacementEditor'
import { Geotile as GeotileUtils } from '../../utils'

import style from './style.css'

function numericKeyValidator( evt ) {
  const inp = String.fromCharCode( evt.charCode );
  const c = evt.charCode;
  if ( /[0-9]/.test( inp ) ) {
    return true;
  }
  return false;
}

class GeotileTileEditor extends React.Component {
  static propTypes = {
    projectId : PropTypes.string.isRequired,
    geotile : PropTypes.object.isRequired,
    getMapAndFeatureData : PropTypes.func.isRequired,
    onDeleteShape : PropTypes.func.isRequired,
    mode : PropTypes.string,
    imageUrl : PropTypes.string,
    imageLoading : PropTypes.bool,
    currentZoom : PropTypes.number
  }

  constructor( props ) {
    super( props )
    this.state = {
      minZoom : 0,
      maxZoom : 20
    }
  }

  update = ( body ) => {
    const { dispatch, projectId, geotile : { id : geotileId } } = this.props
    dispatch( GeotileActions.update( { geotileId, projectId, body } ) )
  }

  changePath = ( path, value, err ) => {
    const body = new FormData()
    body.append( path, value )
    this.update( body )
  }

  changeZoom = ( evt ) => {
    const { data, value } = evt
    if ( value ) {
      const level = parseInt( value )
      this.setState( { [`${data}Zoom`] : level } )
    }
  }

  changeMetadata = ( field, value ) => {
    const { geotile } = this.props
    const { metadata = {} } = geotile
    const nMeta = { ...metadata, [field] : value }
    const body = new FormData()
    body.append( 'metadata', JSON.stringify( nMeta ) )
    this.update( body )
  }

  onChangeOpacity = ( e ) => {
    const { dispatch } = this.props
    dispatch( GeotileActions.updateEditor( { [`${e.data}LayerOpacity`] : e.value / 100 } ) )
  }

  toggleAspect = () => {
    const { dispatch, geotile : { editor : { keepAspect } } } = this.props
    dispatch( GeotileActions.updateEditor( { keepAspect : !keepAspect } ) )
  }

  setDrawMode = ( drawMode ) => {
    const { dispatch } = this.props
    dispatch( GeotileActions.updateEditor( { drawMode } ) )
  }

  onCreateTile = async () => {
    const { dispatch, geotile, getMapAndFeatureData, projectId } = this.props
    const { minZoom, maxZoom } = this.state
    const { sourceImagePath } = geotile
    const { feature } = getMapAndFeatureData()
    if ( !feature ) {
      dispatch( NotifyActions.error( 'Missing boundaries', 'Draw a boundary to place your image before creating a tile' ) )
      return
    } if ( !sourceImagePath ) {
      dispatch( NotifyActions.error( 'Image required', 'Select an image before creating a tile' ) )
      return
    }
    const metadata = geotile.metadata || {}
    const body = await GeotileUtils.createTileFormData( { zoom : { minZoom, maxZoom }, feature, metadata : { ...metadata, tileSize : 512 } } )

    dispatch( GeotileActions.update( { projectId, geotileId : geotile.id, body } ) )
  }

  onSelectImage = e => this.props.onSelectImage( e )

  render() {
    const { geotile, editorState, imageUrl } = this.props
    const { minZoom, maxZoom } = this.state
    const { tileLayerOpacity, currentZoom } = editorState
    const { isGenerating } = geotile
    return (
      <>
        <Section title={geotile.name} subtitle={geotile.id} titleOnTop />
        <Section title="Image" titleOnTop>
          <ImageEditor onChangeOpacity={this.onChangeOpacity} {...this.props} />
        </Section>
        <Section title="Position">
          <PlacementEditor setDrawMode={this.setDrawMode} {...this.props} />
        </Section>
        <Section title="Tile">
          <LabelGroup className={style.sliderGroup}>
            <label>Layer Opacity</label>
            <Slider
              min={0}
              max={100}
              eventData="tile"
              variant="opacity"
              className={style.slider}
              onChange={this.onChangeOpacity}
              value={tileLayerOpacity * 100} />
          </LabelGroup>
          <LabelGroup className={style.sliderGroup}>
            <label>Current Zoom</label>
            <Text>{currentZoom.toFixed( 3 )}</Text>
          </LabelGroup>
          <LabelGroup className={style.sliderGroup}>
            <label>Render tiles through zoom levels:</label>
          </LabelGroup>
          <LabelGroup className={style.sliderGroup}>
            <label>Min Zoom</label>
            <TextInput
              acceptKey={numericKeyValidator}
              eventData="min"
              changeOnReturn={false}
              value={`${minZoom}`}
              onChange={this.changeZoom} />
          </LabelGroup>
          <LabelGroup className={style.sliderGroup}>
            <label>Max Zoom</label>
            <TextInput
              eventData="max"
              changeOnReturn={false}
              acceptKey={numericKeyValidator}
              value={`${maxZoom}`}
              onChange={this.changeZoom} />
          </LabelGroup>
          <div>
            <Button disabled={isGenerating} onClick={this.onCreateTile}>
              {isGenerating ? 'Generating...  ' : 'Generate tiles   '}
              <i className={isGenerating ? 'fa fa-spin fa-refresh fa-fw' : 'fa fa-plus-square-o'} />
            </Button>
          </div>
        </Section>
      </>
    )
  }
}

export default connect( state => ( {
  editorState : state?.geotile?.editor || {},
  geotile : state?.geotile || {}
} ) )( GeotileTileEditor )
